import { createClient } from 'contentful';
import { useEffect, useMemo, useState } from 'react';
import { Document, BLOCKS, Text } from '@contentful/rich-text-types';
import Router, { useRouter } from 'next/router';
import Link from 'next/link';
import Layout from '@/components/layout/Layout';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb';
import { DATASETS } from '..';

export default function Page() {
  const { query } = useRouter();
  const overviewData = DATASETS.find(dataset => dataset.slug === query.dataset);

  if (!overviewData) {
    console.error('INVALID DATASET', query.dataset);
    Router.push('/scout', undefined, { shallow: true });
  }

  const client = useMemo(
    () =>
      createClient({
        space: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID!,
        accessToken: process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN!,
      }),
    [],
  );
  const [overviewContent, setOverviewContent] = useState<string[]>();

  useEffect(() => {
    client
      .getEntries({
        'fields.slug': overviewData?.overviewArticle.slug,
        content_type: 'blogMainContent',
      })
      .then(({ items }) => {
        const fields = items[0].fields;
        const richText = fields.richText as Document;

        // Only render the first 2 paragraphs from the article to fill the quick overview section.
        const overviewBlock = richText.content
          .filter(
            ({ nodeType, content }) =>
              nodeType === BLOCKS.PARAGRAPH &&
              content.length > 0 &&
              !((content[0] as Text).value === '\t'),
          )
          .slice(0, 2)
          .map(({ content }) => (content[0] as Text).value);
        setOverviewContent(overviewBlock);
      });
  }, [client, overviewData?.overviewArticle.slug]);

  return (
    <Layout>
      <div className="max-w-[1000px]">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link href="/scout/datasets">Datasets</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbPage>{overviewData?.feature}</BreadcrumbPage>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Overview section */}
        <div className="mb-6 rounded-lg border border-neutral-700 bg-neutral-900 p-6">
          <div className="mb-4 text-xl font-bold">
            {overviewData?.feature} Overview
          </div>
          <div className="text-lg text-neutral-400">
            {overviewContent?.map((content, i) => (
              <div className="mb-4" key={i}>
                {content}
              </div>
            ))}
          </div>
          <a
            className="mt-4 block font-bold text-azure-500"
            href={`/scout/datasets/${overviewData?.slug}/${overviewData?.overviewArticle.slug}`}>
            Learn more about {overviewData?.feature}
          </a>
        </div>

        {/* Common blog posts */}
        {(overviewData?.commonQuestionArticles.length ?? 0) > 0 && (
          <div className="mb-6 rounded-lg border border-neutral-700 bg-neutral-900 p-6">
            <div className="mb-6 text-xl font-bold">
              Common questions about {overviewData?.feature}
            </div>
            {overviewData?.commonQuestionArticles.map(({ slug, title }) => {
              // Intentional - deterministically generate a fake estiamted time takes to read based on the slug.
              const timeEstimate = Math.max(slug.length % 10, 3);
              return (
                <div key={slug} className="mb-6 flex">
                  <a
                    className="font-bold hover:text-azure-500"
                    href={`/scout/datasets/${overviewData?.slug}/${slug}`}>
                    {title}
                  </a>
                  <div className="ml-4 text-neutral-600">
                    {timeEstimate} min read
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
